import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react';
import api from '../lib/api';
import useAsync from '../hooks/useAsync';
import useContentCategories from '../hooks/useCategories';
import Loader, { ErrorState } from '../components/Loader';
import ImageUploader from '../components/ImageUploader';
import { formatINR } from '../lib/whatsapp';

const blank = { title: '', category: '', price: '', duration: '', summary: '', cover: '', tag: '', rating: 5 };

// Common badge options; admin can also type a custom one.
const TAG_OPTIONS = ['', 'Most Popular', 'Trending', 'Premium', 'New', 'Best Value'];

export default function AdminPackages() {
  const { data: items, loading, error, reload } = useAsync(
    () => api.get('/packages?all=true').then((r) => r.data),
    []
  );
  const categories = useContentCategories();

  const [editing, setEditing] = useState(null); // package _id | 'new' | null
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setForm({ ...blank, category: categories[0]?.name || 'Weddings' });
    setEditing('new');
  };
  const openEdit = (p) => {
    setForm({
      title: p.title, category: p.category, price: p.price, duration: p.duration || '',
      summary: p.summary || '', cover: p.cover || '',
      tag: p.tag || '', rating: p.rating ?? 5,
    });
    setEditing(p._id);
  };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      rating: Math.max(0, Math.min(5, Number(form.rating) || 0)),
    };
    try {
      if (editing === 'new') {
        await api.post('/packages', { ...payload, gallery: payload.cover ? [payload.cover] : [], features: [] });
      } else {
        await api.put(`/packages/${editing}`, payload);
      }
      close();
      reload();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    await api.delete(`/packages/${id}`);
    reload();
  };

  if (loading) return <Loader label="Loading packages…" />;
  if (error) return <ErrorState message="Couldn't load packages." onRetry={reload} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Packages</h1>
          <p className="text-sm text-ink-400">{items.length} packages</p>
        </div>
        <button onClick={openNew} className="btn-primary !py-2.5 text-sm">
          <Plus size={16} /> Add Package
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <div key={p._id} className="glass overflow-hidden">
            {p.cover ? (
              <img src={p.cover} alt={p.title} className="h-36 w-full object-cover" />
            ) : (
              <div className="grid h-36 w-full place-items-center bg-ink-800 text-ink-500">No image</div>
            )}
            <div className="space-y-2 p-4">
              <div className="flex items-center justify-between">
                <span className="rounded-pill bg-white/5 px-2.5 py-1 text-xs text-ink-300">{p.category}</span>
                <span className="text-sm font-semibold text-gradient">{formatINR(p.price)}</span>
              </div>
              <h3 className="font-medium text-white">{p.title}</h3>
              <div className="flex gap-2 pt-2">
                <button onClick={() => openEdit(p)} className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-white/10 py-2 text-xs text-ink-200 hover:bg-white/5">
                  <Pencil size={13} /> Edit
                </button>
                <button onClick={() => remove(p._id)} className="flex items-center justify-center gap-1 rounded-lg border border-danger/30 px-3 py-2 text-xs text-danger hover:bg-danger/10">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur"
            onClick={close}
          >
            <motion.form
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={save}
              className="glass max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto p-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-bold">{editing === 'new' ? 'Add Package' : 'Edit Package'}</h2>
                <button type="button" onClick={close}><X size={20} className="text-ink-400" /></button>
              </div>

              <ImageUploader
                value={form.cover}
                onChange={(url) => setForm({ ...form, cover: url })}
                folder="packages"
                label="Cover Image"
              />

              {[
                { k: 'title', label: 'Title', type: 'text' },
                { k: 'price', label: 'Price (₹)', type: 'number' },
                { k: 'duration', label: 'Duration', type: 'text' },
              ].map((f) => (
                <label key={f.k} className="block">
                  <span className="mb-1 block text-sm text-ink-300">{f.label}</span>
                  <input
                    type={f.type} value={form[f.k]} required={f.k === 'title' || f.k === 'price'}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                  />
                </label>
              ))}

              <label className="block">
                <span className="mb-1 block text-sm text-ink-300">Category</span>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-ink-800 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                >
                  {categories.map((c) => <option key={c.id || c.name}>{c.name}</option>)}
                </select>
              </label>

              {/* Badge / Tag + Rating — controlled by admin */}
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-sm text-ink-300">Badge / Tag</span>
                  <input
                    list="tag-options"
                    value={form.tag}
                    onChange={(e) => setForm({ ...form, tag: e.target.value })}
                    placeholder="None"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand placeholder:text-ink-500"
                  />
                  <datalist id="tag-options">
                    {TAG_OPTIONS.filter(Boolean).map((t) => <option key={t} value={t} />)}
                  </datalist>
                  <span className="mt-1 block text-[11px] text-ink-500">Leave blank to show no badge.</span>
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm text-ink-300">Rating (0–5)</span>
                  <input
                    type="number" min="0" max="5" step="0.1"
                    value={form.rating}
                    onChange={(e) => setForm({ ...form, rating: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1 block text-sm text-ink-300">Summary</span>
                <textarea
                  rows={2} value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                />
              </label>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={close} className="btn-ghost flex-1">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save'}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
